import React, { Component } from 'react'
import cx from 'classnames'
import PropTypes from 'prop-types'

import BackgroundLayer from 'common/builder/components/chart/FlowChart/nodes/shared/BackgroundLayer'
import SelectionFrames from 'common/builder/components/chart/FlowChart/nodes/shared/SelectionFrames'
import FlowSimulator from 'common/builder/components/flowSimulator/components/flowSimulator/FlowSimulator'
import IfFlowSimulatorHidden from 'common/builder/components/flowSimulator/components/ifFlowSimulatorHidden/IfFlowSimulatorHidden'
import { BlockType } from 'common/builder/constants/BlockType'
import FlowBuilderMode from 'common/builder/constants/FlowBuilderMode'
import { analyticsService, flowBuilderEvents } from 'utils/services/analytics'

import app from './app'
import basicFlowChart from './basicFlowChart'
import FlowSelection from './FlowSelection'
import { Node } from './Node'
import PixiStage from './PixiStage'
import PositionManager from './PositionManager'
import { StatsPopover } from './StatsPopover/StatsPopover'
import StatusBar from './StatusBar'
import Toolbar from './Toolbar'

import flowChartCm from './FlowChart.module.css'

class FlowChartViewer extends Component {
  static propTypes = {
    builderId: PropTypes.string,
    activeId: PropTypes.string,
    flowId: PropTypes.any,
    rootId: PropTypes.any,
    sidebarHidden: PropTypes.bool,
    disallowEditMode: PropTypes.bool,
    statisticsViewer: PropTypes.element,
    isPostFlowViewer: PropTypes.bool,

    contents: PropTypes.any,
    contentList: PropTypes.any,
    actionGroups: PropTypes.any,
    actionGroupList: PropTypes.any,
    gotos: PropTypes.any,
    gotosList: PropTypes.any,
    splits: PropTypes.any,
    splitsList: PropTypes.any,
    blocks: PropTypes.any,
    buttons: PropTypes.any,

    coordinates: PropTypes.any,
    selected: PropTypes.any,

    setSelection: PropTypes.func,
    selectSegment: PropTypes.func,
    hideSidebar: PropTypes.func,
    onModeChange: PropTypes.func,
    onNodeClick: PropTypes.func.isRequired,
  }

  constructor(props) {
    super(props)

    this.state = {
      connectors: [],
      selectedElementId: null,
    }
    app.sidebarHidden = props.sidebarHidden || props.player
  }

  componentWillUnmount() {
    app.stopPanning()
  }

  componentDidUpdate(prevProps, prevState) {
    app.render()
  }

  handleButtonClick = (e, btnId, contentId) => {
    const { buttons, player } = this.props
    if (player) {
      const { targetId } = buttons[btnId] || {}
      if (targetId) {
        app.moveTo(targetId)
      }
    } else {
      setTimeout(() => this.setState({ selectedElementId: btnId }))
    }
  }

  handleBlockClick = (e, cardId) => {
    analyticsService.logFlowBuilderEvent(flowBuilderEvents.SELECT_BLOCK, {
      blockId: cardId,
    })

    const block = this.props.blocks[cardId]
    if (!block || ![BlockType.CARD, BlockType.LIST_ITEM].includes(block.type)) {
      return
    }
    setTimeout(() => this.setState({ selectedElementId: cardId }))
  }

  handleContentAnswersClick = (contentId) => {
    this.props.onModeChange(`${FlowBuilderMode.ANSWERS}/${contentId}`)
  }

  handleEditClick = (e) => {
    this.props.onModeChange(FlowBuilderMode.EDIT)
  }

  handleStatsPopoverClose = () => this.setState({ selectedElementId: null })

  handleTransform = (t) => {
    const { transform } = this.props
    transform.x = t.x
    transform.y = t.y
    transform.k = t.k
  }

  handleRootRef = (el) => {
    if (el) {
      this.root = el
      const { width, height } = el.getBoundingClientRect()
      this.setState({ width, height })
    }
  }

  render() {
    const {
      blocks,
      buttons,
      nodes,
      builderId,
      flowId,
      rootId,
      flow,
      coordinates,
      transform,
      withTransform,
      selected,
      setSelection,
      sidebarHidden,
      selectSegment,
      hideSidebar,
      disallowEditMode,
      onNodeClick,
      onStageReady,
      isPostFlowViewer,
      player,
      viewportSize: { width, height } = {},
      newNodes,
      services,
    } = this.props

    const { selectedElementId } = this.state

    const sharedProps = {
      builderId: builderId,
      readonly: true,
      noStat: player,
      coordinates,
      blocks,
      buttons,
      selected,
      onClick: onNodeClick,
      cloneNodes: this.cloneNodes,
      isTargetAllowed: this.props.isTargetAllowed,
    }

    const rootClassName = cx(flowChartCm.root)

    return (
      <div ref={this.handleRootRef} className={rootClassName}>
        {Boolean(width) && (
          <>
            <PixiStage
              width={width}
              height={height}
              onTransform={this.handleTransform}
              initialTransform={transform}
              readonly
              onReady={onStageReady}
              player={player}
              isStaticTextResolution={this.props.builderState.isStaticTextResolution}
            >
              <BackgroundLayer />
              {!player && (
                <>
                  <FlowSelection
                    selected={selected}
                    onSelect={setSelection}
                    hideSidebar={hideSidebar}
                  />
                  <FlowSimulator builderId={builderId} />
                  <IfFlowSimulatorHidden builderId={builderId}>
                    <Toolbar className={flowChartCm.toolbar} readonly />
                  </IfFlowSimulatorHidden>
                </>
              )}

              <StatsPopover
                builderId={builderId}
                entityId={selectedElementId}
                onClose={this.handleStatsPopoverClose}
              />

              <PositionManager
                rootId={rootId}
                coordinates={coordinates}
                nodes={nodes}
                blocks={blocks}
                buttons={buttons}
                noPosition={withTransform}
                immediately={withTransform || player}
              />
              {newNodes.map((node) => (
                <Node
                  key={node.id}
                  node={node}
                  rootId={rootId}
                  foreign={node.flow !== flowId}
                  onButtonClick={this.handleButtonClick}
                  onBlockClick={this.handleBlockClick}
                  onSelectSegment={selectSegment}
                  onAnswersBtnClick={this.handleContentAnswersClick}
                  services={services}
                  {...sharedProps}
                />
              ))}
              <SelectionFrames selected={selected} />
            </PixiStage>

            {!player && (
              <StatusBar
                readonly
                disallowEditMode={disallowEditMode}
                isPostFlowViewer={isPostFlowViewer}
                flow={flow}
                selected={selected}
                sidebarHidden={sidebarHidden}
                onEdit={this.handleEditClick}
              />
            )}
          </>
        )}
      </div>
    )
  }
}

export default basicFlowChart(FlowChartViewer)
